import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Save, Share2, Eye, User, Mail, Phone, Linkedin, 
  Briefcase, MapPin, Award, Star, Languages, Heart, TrendingUp,
  Plus, X, Check, Copy, ExternalLink, Twitter, Github, Globe,
  Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import TokenUsageSection from '../components/TokenUsageSection';
import { MyTickets } from '../components/support/MyTickets';
import { RecruiterScoreCard } from '../components/profile/RecruiterScoreCard';
import { Career360Section } from '../components/profile/Career360Section';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import BACKEND_URL from '../config/api';
import { useToast } from '../hooks/use-toast';

const EnhancedProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, token } = useAuth();
  const { toast } = useToast();
  
  const [updating, setUpdating] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    // Basic
    name: '',
    phone: '',
    email: '',
    job_title: '',
    tagline: '',
    current_company: '',
    location: '',
    total_experience: '',
    bio: '',
    
    // Social
    linkedin_url: '',
    social_links: {
      twitter: '',
      github: '',
      portfolio: ''
    },
    
    // Professional
    skills: [],
    expertise: [],
    career_interests: [],
    
    // Additional
    languages: [],
    hobbies: [],
    experience: [],
    achievements: [],
    testimonials: [],
    featured_stat: { label: '', description: '' },
    availability: '',
    
    // Theme
    profile_theme: 'professional_blue'
  });
  
  // Temp inputs
  const [newSkill, setNewSkill] = useState('');
  const [newExpertise, setNewExpertise] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [newHobby, setNewHobby] = useState('');
  const [newAchievement, setNewAchievement] = useState('');
  const [newLanguage, setNewLanguage] = useState({ language: '', proficiency: 'Professional' });
  const [newExperience, setNewExperience] = useState({
    company: '',
    title: '',
    start_date: '',
    end_date: '',
    description: '',
    location: ''
  });

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchProfile();
    }
  }, [isAuthenticated, token]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setProfileData(data.profile);
        setFormData({
          name: data.profile.name || '',
          phone: data.profile.phone || '',
          email: data.profile.email || '',
          job_title: data.profile.job_title || '',
          tagline: data.profile.tagline || '',
          current_company: data.profile.current_company || '',
          location: data.profile.location || '',
          total_experience: data.profile.total_experience || '',
          bio: data.profile.bio || '',
          linkedin_url: data.profile.linkedin_url || '',
          social_links: data.profile.social_links || { twitter: '', github: '', portfolio: '' },
          skills: data.profile.skills || [],
          expertise: data.profile.expertise || [],
          career_interests: data.profile.career_interests || [],
          languages: data.profile.languages || [],
          hobbies: data.profile.hobbies || [],
          experience: data.profile.experience || [],
          achievements: data.profile.achievements || [],
          testimonials: data.profile.testimonials || [],
          featured_stat: data.profile.featured_stat || { label: '', description: '' },
          availability: data.profile.availability || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSocialChange = (platform, value) => {
    setFormData({
      ...formData,
      social_links: {
        ...formData.social_links,
        [platform]: value
      }
    });
  };

  const handleFeaturedStatChange = (field, value) => {
    setFormData({
      ...formData,
      featured_stat: {
        ...formData.featured_stat,
        [field]: value
      }
    });
  };

  // Add/Remove handlers
  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData({ ...formData, skills: [...formData.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
  };

  const addExpertise = () => {
    if (newExpertise.trim() && !formData.expertise.includes(newExpertise.trim())) {
      setFormData({ ...formData, expertise: [...formData.expertise, newExpertise.trim()] });
      setNewExpertise('');
    }
  };

  const removeExpertise = (item) => {
    setFormData({ ...formData, expertise: formData.expertise.filter(e => e !== item) });
  };

  const addInterest = () => {
    if (newInterest.trim() && !formData.career_interests.includes(newInterest.trim())) {
      setFormData({ ...formData, career_interests: [...formData.career_interests, newInterest.trim()] });
      setNewInterest('');
    }
  };

  const removeInterest = (item) => {
    setFormData({ ...formData, career_interests: formData.career_interests.filter(i => i !== item) });
  };

  const addHobby = () => {
    if (newHobby.trim() && !formData.hobbies.includes(newHobby.trim())) {
      setFormData({ ...formData, hobbies: [...formData.hobbies, newHobby.trim()] });
      setNewHobby('');
    }
  };

  const removeHobby = (hobby) => {
    setFormData({ ...formData, hobbies: formData.hobbies.filter(h => h !== hobby) });
  };

  const addAchievement = () => {
    if (newAchievement.trim()) {
      setFormData({ ...formData, achievements: [...formData.achievements, newAchievement.trim()] });
      setNewAchievement('');
    }
  };

  const removeAchievement = (idx) => {
    setFormData({ ...formData, achievements: formData.achievements.filter((_, i) => i !== idx) });
  };

  const addLanguage = () => {
    if (newLanguage.language.trim()) {
      setFormData({ ...formData, languages: [...formData.languages, newLanguage] });
      setNewLanguage({ language: '', proficiency: 'Professional' });
    }
  };

  const removeLanguage = (idx) => {
    setFormData({ ...formData, languages: formData.languages.filter((_, i) => i !== idx) });
  };

  const addExperience = () => {
    if (newExperience.company && newExperience.title && newExperience.start_date) {
      setFormData({ ...formData, experience: [...formData.experience, newExperience] });
      setNewExperience({ company: '', title: '', start_date: '', end_date: '', description: '', location: '' });
    }
  };

  const removeExperience = (idx) => {
    setFormData({ ...formData, experience: formData.experience.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success!',
          description: 'Profile updated successfully'
        });
        fetchProfile(); // Refresh to get updated completion %
      } else {
        throw new Error(data.message || 'Update failed');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleShareProfile = () => {
    const url = `${window.location.origin}/profile/${profileData.user_id}/public`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast({
      title: 'Link Copied!',
      description: 'Your public profile URL has been copied to clipboard'
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewPublicProfile = () => {
    window.open(`/profile/${profileData.user_id}/public`, '_blank');
  };

  if (loading || !profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 py-8 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>

          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">My Profile</h1>
              <p className="text-slate-600">Your brand, career preferences and Recruiter Score — all in one place</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                data-testid="profile-preview-btn"
                onClick={handleViewPublicProfile}
                variant="outline"
                size="sm"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                data-testid="profile-share-btn"
                onClick={handleShareProfile}
                variant="outline"
                size="sm"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                Share Profile
              </Button>
            </div>
          </div>
        </div>

        {/* Unified Recruiter Score */}
        <RecruiterScoreCard scoreData={profileData.recruiter_score} />

        <Tabs defaultValue="brand" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 h-12">
            <TabsTrigger data-testid="tab-personal-brand" value="brand" className="text-sm font-semibold">Personal Brand</TabsTrigger>
            <TabsTrigger data-testid="tab-career-360" value="career" className="text-sm font-semibold">Career 360°</TabsTrigger>
          </TabsList>

          <TabsContent value="career">
            <Career360Section onSaved={fetchProfile} />
          </TabsContent>

          <TabsContent value="brand">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* LinkedIn Auto-Fill Section */}
          <Card className="shadow-lg border-2 border-blue-300">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
              <CardTitle className="flex items-center gap-2">
                <Linkedin className="w-5 h-5 text-blue-600" />
                LinkedIn Profile Auto-Fill
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">Import your professional information from LinkedIn with one click</p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Input
                  id="linkedin_import_url"
                  placeholder="https://linkedin.com/in/yourprofile"
                  className="flex-1"
                />
                <Button 
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={async () => {
                    const linkedinUrl = document.getElementById('linkedin_import_url').value;
                    if (!linkedinUrl) {
                      toast({
                        title: 'Error',
                        description: 'Please enter your LinkedIn profile URL',
                        variant: 'destructive'
                      });
                      return;
                    }
                    
                    try {
                      const response = await fetch(`${BACKEND_URL}/api/user/parse-linkedin`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ linkedin_url: linkedinUrl })
                      });
                      
                      const data = await response.json();
                      
                      if (data.success && data.profile_data) {
                        setFormData({
                          ...formData,
                          name: data.profile_data.name || formData.name,
                          job_title: data.profile_data.job_title || formData.job_title,
                          current_company: data.profile_data.current_company || formData.current_company,
                          total_experience: data.profile_data.total_experience || formData.total_experience,
                          bio: data.profile_data.bio || formData.bio,
                          skills: data.profile_data.skills || formData.skills,
                          linkedin_url: linkedinUrl
                        });
                        
                        toast({
                          title: 'Success!',
                          description: 'LinkedIn profile data imported successfully'
                        });
                      } else {
                        throw new Error(data.message || 'Failed to parse LinkedIn profile');
                      }
                    } catch (error) {
                      toast({
                        title: 'Error',
                        description: error.message || 'Failed to import LinkedIn data',
                        variant: 'destructive'
                      });
                    }
                  }}
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  Auto-Fill
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Theme Selector Section */}
          <Card className="shadow-lg border-2 border-purple-300">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-600" />
                Profile Theme
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1">Choose how your public profile looks</p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { id: 'professional_blue', name: 'Professional Blue', colors: ['bg-blue-500', 'bg-blue-600'] },
                  { id: 'modern_purple', name: 'Modern Purple', colors: ['bg-purple-500', 'bg-pink-500'] },
                  { id: 'elegant_gray', name: 'Elegant Gray', colors: ['bg-slate-600', 'bg-slate-800'] },
                  { id: 'creative_gradient', name: 'Creative Gradient', colors: ['bg-cyan-500', 'bg-blue-600'] }
                ].map(theme => (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, profile_theme: theme.id })}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      formData.profile_theme === theme.id 
                        ? 'border-purple-500 shadow-lg' 
                        : 'border-slate-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex gap-2 mb-2">
                      <div className={`w-8 h-8 rounded ${theme.colors[0]}`}></div>
                      <div className={`w-8 h-8 rounded ${theme.colors[1]}`}></div>
                    </div>
                    <p className="text-xs font-medium text-slate-700">{theme.name}</p>
                    {formData.profile_theme === theme.id && (
                      <Check className="w-4 h-4 text-purple-600 mx-auto mt-1" />
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email (Read-only)</Label>
                  <Input
                    id="email"
                    value={formData.email}
                    disabled
                    className="bg-slate-100"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="job_title">Job Title</Label>
                  <Input
                    id="job_title"
                    name="job_title"
                    value={formData.job_title}
                    onChange={handleChange}
                    placeholder="Senior Technical Recruiter"
                  />
                </div>
                <div>
                  <Label htmlFor="current_company">Current Company</Label>
                  <Input
                    id="current_company"
                    name="current_company"
                    value={formData.current_company}
                    onChange={handleChange}
                    placeholder="TechCorp Inc."
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="San Francisco, CA"
                  />
                </div>
                <div>
                  <Label htmlFor="total_experience">Years of Experience</Label>
                  <Input
                    id="total_experience"
                    name="total_experience"
                    type="number"
                    value={formData.total_experience}
                    onChange={handleChange}
                    placeholder="5"
                    min="0"
                    step="0.5"
                  />
                </div>
                <div>
                  <Label htmlFor="availability">Availability</Label>
                  <Input
                    id="availability"
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    placeholder="Open to opportunities"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="tagline">Professional Tagline</Label>
                <Input
                  id="tagline"
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleChange}
                  placeholder="Connecting top talent with innovative companies"
                  maxLength={150}
                />
                <p className="text-xs text-slate-500 mt-1">{formData.tagline?.length || 0}/150</p>
              </div>
              
              <div>
                <Label htmlFor="bio">Bio / About Me</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell the world about yourself, your passion for recruitment, and what makes you unique..."
                  rows={5}
                  maxLength={1000}
                />
                <p className="text-xs text-slate-500 mt-1">{formData.bio?.length || 0}/1000</p>
              </div>
            </CardContent>
          </Card>

          {/* Social Links */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-purple-600" />
                Social Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="linkedin_url">LinkedIn Profile</Label>
                <div className="flex items-center gap-2">
                  <Linkedin className="w-5 h-5 text-blue-600" />
                  <Input
                    id="linkedin_url"
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="twitter">Twitter Profile</Label>
                <div className="flex items-center gap-2">
                  <Twitter className="w-5 h-5 text-blue-400" />
                  <Input
                    id="twitter"
                    value={formData.social_links.twitter}
                    onChange={(e) => handleSocialChange('twitter', e.target.value)}
                    placeholder="https://twitter.com/yourhandle"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="github">GitHub Profile</Label>
                <div className="flex items-center gap-2">
                  <Github className="w-5 h-5 text-slate-800" />
                  <Input
                    id="github"
                    value={formData.social_links.github}
                    onChange={(e) => handleSocialChange('github', e.target.value)}
                    placeholder="https://github.com/yourusername"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="portfolio">Portfolio / Website</Label>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <Input
                    id="portfolio"
                    value={formData.social_links.portfolio}
                    onChange={(e) => handleSocialChange('portfolio', e.target.value)}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Featured Stat */}
          <Card className="shadow-lg border-2 border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-yellow-600" />
                Featured Stat (Your Highlight)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="featured_label">Label (e.g., "100+")</Label>
                  <Input
                    id="featured_label"
                    value={formData.featured_stat.label}
                    onChange={(e) => handleFeaturedStatChange('label', e.target.value)}
                    placeholder="100+"
                  />
                </div>
                <div>
                  <Label htmlFor="featured_desc">Description</Label>
                  <Input
                    id="featured_desc"
                    value={formData.featured_stat.description}
                    onChange={(e) => handleFeaturedStatChange('description', e.target.value)}
                    placeholder="Successful Placements"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-600">This will appear prominently on your public profile</p>
            </CardContent>
          </Card>

          {/* Skills */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-blue-600" />
                Skills (Max 30)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Add a skill (e.g., Technical Recruiting)"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                />
                <Button type="button" onClick={addSkill} disabled={formData.skills.length >= 30}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.skills.map((skill, idx) => (
                  <Badge key={idx} className="bg-blue-100 text-blue-700 hover:bg-blue-200">
                    {skill}
                    <X 
                      className="w-3 h-3 ml-2 cursor-pointer" 
                      onClick={() => removeSkill(skill)}
                    />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Languages */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Languages className="w-5 h-5 text-purple-600" />
                Languages
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newLanguage.language}
                  onChange={(e) => setNewLanguage({ ...newLanguage, language: e.target.value })}
                  placeholder="Language"
                  className="flex-1"
                />
                <select
                  value={newLanguage.proficiency}
                  onChange={(e) => setNewLanguage({ ...newLanguage, proficiency: e.target.value })}
                  className="px-3 py-2 border border-slate-300 rounded-md"
                >
                  <option value="Native">Native</option>
                  <option value="Fluent">Fluent</option>
                  <option value="Professional">Professional</option>
                  <option value="Basic">Basic</option>
                </select>
                <Button type="button" onClick={addLanguage}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {formData.languages.map((lang, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div>
                      <span className="font-medium">{lang.language}</span>
                      <Badge className="ml-2 bg-purple-100 text-purple-700">{lang.proficiency}</Badge>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLanguage(idx)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Work Experience */}
          <Card className="shadow-lg border-2 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-green-600" />
                Work Experience (Max 10)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    value={newExperience.company}
                    onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })}
                    placeholder="Company Name *"
                  />
                  <Input
                    value={newExperience.title}
                    onChange={(e) => setNewExperience({ ...newExperience, title: e.target.value })}
                    placeholder="Job Title *"
                  />
                  <Input
                    value={newExperience.start_date}
                    onChange={(e) => setNewExperience({ ...newExperience, start_date: e.target.value })}
                    placeholder="Start Date (e.g., Jan 2020) *"
                  />
                  <Input
                    value={newExperience.end_date}
                    onChange={(e) => setNewExperience({ ...newExperience, end_date: e.target.value })}
                    placeholder="End Date (or 'Present')"
                  />
                  <Input
                    value={newExperience.location}
                    onChange={(e) => setNewExperience({ ...newExperience, location: e.target.value })}
                    placeholder="Location (optional)"
                    className="md:col-span-2"
                  />
                </div>
                <Textarea
                  value={newExperience.description}
                  onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })}
                  placeholder="Brief description of your role and achievements"
                  rows={3}
                />
                <Button 
                  type="button" 
                  onClick={addExperience} 
                  disabled={formData.experience.length >= 10}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Experience
                </Button>
              </div>
              
              <div className="space-y-4">
                {formData.experience.map((exp, idx) => (
                  <div key={idx} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 relative">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExperience(idx)}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    <h4 className="font-bold text-lg text-slate-900">{exp.title}</h4>
                    <p className="text-green-700 font-semibold">{exp.company}</p>
                    <p className="text-sm text-slate-600 mb-2">
                      {exp.start_date} - {exp.end_date || 'Present'}
                      {exp.location && ` • ${exp.location}`}
                    </p>
                    {exp.description && (
                      <p className="text-sm text-slate-700 mt-2">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hobbies */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-600" />
                Hobbies & Interests (Max 15)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newHobby}
                  onChange={(e) => setNewHobby(e.target.value)}
                  placeholder="Add a hobby"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHobby())}
                />
                <Button type="button" onClick={addHobby} disabled={formData.hobbies.length >= 15}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.hobbies.map((hobby, idx) => (
                  <Badge key={idx} className="bg-pink-100 text-pink-700 hover:bg-pink-200">
                    {hobby}
                    <X 
                      className="w-3 h-3 ml-2 cursor-pointer" 
                      onClick={() => removeHobby(hobby)}
                    />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Achievements */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-600" />
                Achievements (Max 10)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newAchievement}
                  onChange={(e) => setNewAchievement(e.target.value)}
                  placeholder="Add an achievement"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAchievement())}
                />
                <Button type="button" onClick={addAchievement} disabled={formData.achievements.length >= 10}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <ul className="space-y-2">
                {formData.achievements.map((achievement, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg">
                    <span className="text-yellow-600 mt-0.5">★</span>
                    <span className="flex-1 text-sm">{achievement}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAchievement(idx)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Expertise & Career Interests (keeping original) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Expertise Areas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newExpertise}
                    onChange={(e) => setNewExpertise(e.target.value)}
                    placeholder="Add expertise"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExpertise())}
                  />
                  <Button type="button" onClick={addExpertise}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.expertise.map((item, idx) => (
                    <Badge key={idx} className="bg-green-100 text-green-700">
                      {item}
                      <X 
                        className="w-3 h-3 ml-2 cursor-pointer" 
                        onClick={() => removeExpertise(item)}
                      />
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>Career Interests</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    placeholder="Add interest"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                  />
                  <Button type="button" onClick={addInterest}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.career_interests.map((item, idx) => (
                    <Badge key={idx} className="bg-orange-100 text-orange-700">
                      {item}
                      <X 
                        className="w-3 h-3 ml-2 cursor-pointer" 
                        onClick={() => removeInterest(item)}
                      />
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button
              type="submit"
              size="lg"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={updating}
            >
              {updating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </form>
          </TabsContent>
        </Tabs>

        {/* Token Usage Analytics Section */}
        <div className="mt-8">
          <TokenUsageSection userId={user?.user_id} />

          <MyTickets />
        </div>
      </div>
    </div>
  );
};

export default EnhancedProfilePage;
